import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, X, RefreshCw, Gift, CreditCard, CheckCircle2, Ban, Trash2,
  ExternalLink, User, Building2, Target, Users, KeyRound, Sparkles,
  Map, ShieldCheck, Circle, Mail, Clock, ArrowLeft,
} from "lucide-react";
import { adminAxios, formatINR, relativeTime, PlanBadge } from "./adminApi";

const TABS = [
  { id: "overview", label: "Overview", icon: User },
  { id: "onboarding", label: "Onboarding & Brand", icon: Sparkles },
  { id: "scans", label: "Scans", icon: Target },
  { id: "ledger", label: "Credit ledger", icon: KeyRound },
];

const MARKET_SCOPE_LABEL = {
  local_city: "Local city",
  all_india: "All India",
  in: "India",
  us: "United States",
  global: "Global",
};

/**
 * `value` may be a scalar, a React node, or a string like "" / null / undefined.
 * Renders "Not set" (dim, italic) if empty.
 */
function Val({ value, mono, className = "" }) {
  const isEmpty =
    value === null || value === undefined ||
    (typeof value === "string" && !value.trim()) ||
    (Array.isArray(value) && value.length === 0);
  if (isEmpty) {
    return <span className="text-lm-text3 italic text-sm">Not set</span>;
  }
  return (
    <span className={`text-lm-text ${mono ? "font-mono text-xs" : "text-sm"} ${className}`}>
      {value}
    </span>
  );
}

function KV({ label, children, mono }) {
  return (
    <div className="flex items-start px-3 py-2 gap-3 border-b border-lm-border/40 last:border-b-0">
      <div className="w-40 shrink-0 text-[11px] font-mono uppercase tracking-widest text-lm-text3 pt-0.5">{label}</div>
      <div className="flex-1 min-w-0">{typeof children === "string" || typeof children === "number" ? <Val value={children} mono={mono} /> : children}</div>
    </div>
  );
}

function SectionCard({ title, icon: Ico, children }) {
  return (
    <div className="rounded-lg border border-lm-border bg-lm-bg">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-lm-border bg-lm-tint/40">
        {Ico && <Ico className="w-3.5 h-3.5 text-lm-primary" />}
        <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text2">{title}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function StatusDot({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${
      ok
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-lm-tint text-lm-text3 border-lm-border"
    }`}>
      <Circle className={`w-2 h-2 ${ok ? "fill-emerald-600 text-emerald-600" : "fill-lm-text3 text-lm-text3"}`} />
      {label}
    </span>
  );
}

function ChipList({ items, tint = false }) {
  if (!items?.length) return <Val value={null} />;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((k, i) => (
        <span
          key={typeof k === "string" ? k + i : i}
          className={`text-[11px] px-1.5 py-0.5 rounded font-mono ${
            tint ? "bg-lm-tint2 text-lm-primary border border-lm-tint2" : "bg-lm-tint text-lm-text2 border border-lm-border"
          }`}
        >
          {k}
        </span>
      ))}
    </div>
  );
}

export default function UserDetailDrawer({ userId, onClose, onAction }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await adminAxios().get(`/users/${userId}/detail`);
        setDetail(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load user");
      } finally { setLoading(false); }
    })();
  }, [userId]);

  const u = detail?.user;
  const ws = detail?.workspace;
  const brandBundle = detail?.brand;
  const brand = brandBundle?.brand;
  const profile = brandBundle?.profile;
  const rowUser = u && ws ? {
    ...u, ...ws,
    workspace_id: ws.workspace_id, plan: ws.plan, workspace_name: ws.name,
  } : null;

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="user-drawer">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative ml-auto w-full max-w-3xl bg-lm-surface h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-lm-surface border-b border-lm-border px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">User detail</div>
            <div className="font-display font-semibold truncate">{u?.email || (loading ? "Loading…" : "User")}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="user-drawer-close"
            className="p-2 hover:bg-lm-tint rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading || !detail ? (
          <div className="flex-1 p-8 text-center text-lm-text2"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <>
            {/* Tab bar */}
            <div className="sticky top-[73px] bg-lm-surface border-b border-lm-border z-10 px-6 flex gap-1 overflow-x-auto">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`drawer-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-sm border-b-2 -mb-px whitespace-nowrap transition-colors ${
                    tab === t.id
                      ? "border-lm-primary text-lm-primary font-medium"
                      : "border-transparent text-lm-text3 hover:text-lm-text hover:border-lm-border"
                  }`}
                >
                  <t.icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              ))}
            </div>

            {/* Persistent actions */}
            {rowUser && (
              <div className="px-6 pt-4 flex flex-wrap gap-2">
                <NudgeButton
                  userId={rowUser.user_id}
                  nudge={detail.nudge}
                  onSent={async () => {
                    // Refresh nudge state
                    try {
                      const r = await adminAxios().get(`/users/${userId}/detail`);
                      setDetail(r.data);
                    } catch { /* ignore */ }
                  }}
                />
                <button onClick={() => onAction("reset", rowUser)} data-testid="drawer-action-reset" className="text-xs px-3 py-1.5 rounded-md border border-lm-border hover:bg-lm-tint inline-flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> Reset quota
                </button>
                <button onClick={() => onAction("grant", rowUser)} data-testid="drawer-action-grant" className="text-xs px-3 py-1.5 rounded-md border border-lm-border hover:bg-lm-tint inline-flex items-center gap-1.5">
                  <Gift className="w-3.5 h-3.5" /> Grant scans
                </button>
                <button onClick={() => onAction("plan", rowUser)} data-testid="drawer-action-plan" className="text-xs px-3 py-1.5 rounded-md border border-lm-border hover:bg-lm-tint inline-flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5" /> Change plan
                </button>
                <button
                  onClick={() => onAction("suspend", rowUser)}
                  data-testid="drawer-action-suspend"
                  className={`text-xs px-3 py-1.5 rounded-md border inline-flex items-center gap-1.5 ${
                    u.suspended
                      ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      : "border-red-200 text-red-700 hover:bg-red-50"
                  }`}
                >
                  {u.suspended ? <><CheckCircle2 className="w-3.5 h-3.5" /> Unsuspend</> : <><Ban className="w-3.5 h-3.5" /> Suspend</>}
                </button>
                <button onClick={() => onAction("delete", rowUser)} data-testid="drawer-action-delete" className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" /> Delete account
                </button>
              </div>
            )}

            <div className="flex-1 p-6 space-y-4">
              {tab === "overview" && <OverviewTab u={u} ws={ws} revenue={detail.revenue} brandBundle={brandBundle} onGoBrand={() => setTab("onboarding")} />}
              {tab === "onboarding" && <OnboardingTab ws={ws} brandBundle={brandBundle} onBack={() => setTab("overview")} />}
              {tab === "scans" && <ScansTab scans={detail.scans} />}
              {tab === "ledger" && <LedgerTab ledger={detail.ledger} />}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

// ================= Overview =================
function OverviewTab({ u, ws, revenue, brandBundle, onGoBrand }) {
  const brand = brandBundle?.brand;
  const profile = brandBundle?.profile;
  const liveKw = brandBundle?.keywords_live || [];
  const totalKw = brandBundle?.keywords_total ?? (brandBundle?.keywords?.length || 0);
  return (
    <div className="space-y-4">
      {/* Active brand — front-and-center so admin sees what they're working with */}
      <div className="rounded-lg border border-lm-primary/25 bg-lm-tint2/40 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-lm-primary/15">
          <Building2 className="w-3.5 h-3.5 text-lm-primary" />
          <div className="text-[11px] font-mono uppercase tracking-widest text-lm-primary">Active brand</div>
          {brand && (
            <span className={`ml-auto text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${
              brand.status === "confirmed"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-amber-50 text-amber-800 border-amber-200"
            }`}>{brand.status || "draft"}</span>
          )}
        </div>
        {brand ? (
          <div className="p-3 space-y-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display font-semibold text-lm-text truncate">{brand.name || "Untitled brand"}</div>
                {brand.website_url && (
                  <a href={brand.website_url} target="_blank" rel="noreferrer" className="text-xs text-lm-primary hover:underline inline-flex items-center gap-1 break-all">
                    {brand.website_url}<ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                )}
              </div>
              <button onClick={onGoBrand} className="shrink-0 text-[11px] px-2 py-1 rounded border border-lm-border bg-lm-surface hover:bg-lm-tint text-lm-text2">
                Full brand →
              </button>
            </div>
            {(profile?.category || ws?.business_category_label) && (
              <div className="text-xs text-lm-text2">{profile?.category || ws?.business_category_label}</div>
            )}
            {/* Live keywords */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <KeyRound className="w-3 h-3 text-lm-primary" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">
                  Live keywords · {liveKw.length} of {totalKw} ride every scan
                </span>
              </div>
              {liveKw.length === 0 ? (
                <span className="text-xs text-lm-text3 italic">No keywords ticked live.</span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {liveKw.map((k, i) => (
                    <span key={k + i} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded font-mono bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle2 className="w-2.5 h-2.5" />{k}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-3 text-sm text-lm-text3 italic">No brand imported yet.</div>
        )}
      </div>

      <SectionCard title="Profile" icon={User}>
        <KV label="Email">{u.email}</KV>
        <KV label="Email verified">
          {u.email_verified
            ? <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"><Circle className="w-2 h-2 fill-emerald-600 text-emerald-600" />Verified</span>
            : <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"><Circle className="w-2 h-2 fill-amber-600 text-amber-600" />Not verified</span>}
        </KV>
        <KV label="Name">{u.name}</KV>
        <KV label="User ID" mono>{u.user_id}</KV>
        <KV label="Status">
          {u.suspended
            ? <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">Suspended</span>
            : <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>}
        </KV>
        <KV label="Signup">{u.created_at ? new Date(u.created_at).toLocaleString() : null}</KV>
        <KV label="Last active">{relativeTime(u.last_active_at)}</KV>
      </SectionCard>

      {ws && (
        <SectionCard title="Workspace summary" icon={Building2}>
          <KV label="Workspace">{ws.name}</KV>
          <KV label="Workspace ID" mono>{ws.workspace_id}</KV>
          <KV label="Plan"><PlanBadge plan={ws.plan} /></KV>
          <KV label="Credits balance">{ws.credits_balance ?? null}</KV>
          <KV label="Top-up balance"><span className="font-mono">{ws.topup_scans_balance ?? 0} scans</span></KV>
          <KV label="Revenue (paid)">{`${formatINR(revenue?.total_paise || 0)} · ${revenue?.txn_count || 0} txns`}</KV>
        </SectionCard>
      )}
    </div>
  );
}

// ================= Onboarding & Brand =================
function OnboardingTab({ ws, brandBundle, onBack }) {
  const brand = brandBundle?.brand;
  const profile = brandBundle?.profile;
  const icps = brandBundle?.icps || [];
  const keywords = brandBundle?.keywords || [];
  const kwDetail = brandBundle?.keywords_detail || keywords.map((t) => ({ text: t, live: true }));
  const liveCount = brandBundle?.keywords_live_count ?? kwDetail.filter((k) => k.live).length;
  const competitors = brandBundle?.competitors || [];

  // Progress checklist
  const checklist = [
    { label: "Business category", done: !!ws?.business_category },
    { label: "Market scope", done: !!ws?.market_scope },
    { label: "Brand foundation created", done: !!brand },
    { label: "Brand foundation confirmed", done: brand?.status === "confirmed" },
    { label: "Brand profile filled", done: !!profile },
    { label: "ICP(s) added", done: icps.length > 0 },
  ];
  const doneCount = checklist.filter((c) => c.done).length;
  const pct = Math.round((doneCount / checklist.length) * 100);

  return (
    <div className="space-y-4">
      {onBack && (
        <button
          onClick={onBack}
          data-testid="drawer-brand-back"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-lm-border bg-lm-surface hover:bg-lm-tint text-lm-text2"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to overview
        </button>
      )}
      {/* Progress bar */}
      <div className="rounded-lg border border-lm-border bg-lm-tint2/40 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3">Onboarding completion</div>
          <div className="text-xs font-mono font-semibold text-lm-primary">{doneCount}/{checklist.length} · {pct}%</div>
        </div>
        <div className="h-1.5 rounded-full bg-lm-tint overflow-hidden">
          <div className="h-full bg-lm-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {checklist.map((c) => <StatusDot key={c.label} ok={c.done} label={c.label} />)}
        </div>
      </div>

      {/* Step 1 — Business Category */}
      <SectionCard title="Step 1 · Business category" icon={ShieldCheck}>
        <KV label="Category">
          {ws?.business_category
            ? <span className="text-sm text-lm-text">{ws.business_category_label || ws.business_category}</span>
            : <Val value={null} />}
        </KV>
        <KV label="Category ID" mono>{ws?.business_category}</KV>
        <KV label="Serve tier">
          {ws?.serve_tier != null
            ? <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-lm-tint2 text-lm-primary border border-lm-tint2">Tier {ws.serve_tier}</span>
            : <Val value={null} />}
        </KV>
        <KV label="Custom description">{ws?.business_category_description}</KV>
        <KV label="Tier selected at">{ws?.serve_tier_selected_at ? new Date(ws.serve_tier_selected_at).toLocaleString() : null}</KV>
        <KV label="Tier declined">{ws?.serve_tier_declined ? "Yes — user opted out" : "No"}</KV>
        {ws?.declined_at && <KV label="Declined at">{new Date(ws.declined_at).toLocaleString()}</KV>}
        {ws?.notify_when_available && (
          <>
            <KV label="Notify when available">Yes</KV>
            <KV label="Notify email">{ws?.notify_email}</KV>
            {ws?.notify_optin_at && <KV label="Notify opt-in at">{new Date(ws.notify_optin_at).toLocaleString()}</KV>}
          </>
        )}
      </SectionCard>

      {/* Step 2 — Market Scope */}
      <SectionCard title="Step 2 · Market scope" icon={Map}>
        <KV label="Scope">
          {ws?.market_scope
            ? <span className="text-sm text-lm-text">{MARKET_SCOPE_LABEL[ws.market_scope] || ws.market_scope}</span>
            : <Val value={null} />}
        </KV>
        <KV label="City">{ws?.market_city}</KV>
        <KV label="Selected at">{ws?.market_scope_selected_at ? new Date(ws.market_scope_selected_at).toLocaleString() : null}</KV>
      </SectionCard>

      {/* Step 3 — Brand Foundation */}
      <SectionCard title="Step 3 · Brand foundation" icon={Building2}>
        <KV label="Brand name">{brand?.name}</KV>
        <KV label="Website URL">
          {brand?.website_url
            ? <a href={brand.website_url} target="_blank" rel="noreferrer" className="text-sm text-lm-primary hover:underline inline-flex items-center gap-1 break-all">{brand.website_url}<ExternalLink className="w-3 h-3 shrink-0" /></a>
            : <Val value={null} />}
        </KV>
        <KV label="Description">{brand?.description}</KV>
        <KV label="Household name brand">
          {brand ? (brand.household_name_brand ? "Yes — routes to X/Twitter" : "No") : <Val value={null} />}
        </KV>
        <KV label="Status">
          {brand?.status
            ? <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${
                brand.status === "confirmed"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-amber-50 text-amber-800 border-amber-200"
              }`}>{brand.status}</span>
            : <Val value={null} />}
        </KV>
        <KV label="Brand ID" mono>{brand?.brand_id}</KV>
        <KV label="Created at">{brand?.created_at ? new Date(brand.created_at).toLocaleString() : null}</KV>
        <KV label="Updated at">{brand?.updated_at ? new Date(brand.updated_at).toLocaleString() : null}</KV>
      </SectionCard>

      {/* Step 3.5 — Brand Profile / AI enrichment */}
      <SectionCard title="Step 3b · Brand profile (AI enrichment)" icon={Sparkles}>
        <KV label="Category">{profile?.category}</KV>
        <KV label="Brand summary">{profile?.brand_summary}</KV>
        <KV label="Value props"><ChipList items={profile?.value_props} tint /></KV>
        <KV label="Differentiators"><ChipList items={profile?.differentiators} /></KV>
        <KV label="Price point">
          {profile?.price_point
            ? <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-lm-tint text-lm-text2 border border-lm-border">{profile.price_point}</span>
            : <Val value={null} />}
        </KV>
        <KV label="Target geography">{profile?.target_geography}</KV>
        <KV label="Profile status">
          {profile?.status
            ? <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-lm-tint2 text-lm-primary border border-lm-tint2">{profile.status}</span>
            : <Val value={null} />}
        </KV>
      </SectionCard>

      {/* Keywords — live (ticked) shown in green, inactive dimmed */}
      <SectionCard title={`Keywords · ${liveCount} live of ${kwDetail.length}`} icon={KeyRound}>
        <div className="p-3">
          {kwDetail.length === 0 ? (
            <Val value={null} />
          ) : (
            <>
              <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3 mb-1.5">
                Live keywords ride every scan · unticked ones are saved but idle
              </div>
              <div className="flex flex-wrap gap-1">
                {kwDetail.map((k, i) => (
                  <span
                    key={(k.text || "") + i}
                    title={k.live ? "Live — rides every scan" : "Saved but not ticked"}
                    className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded font-mono border ${
                      k.live
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-lm-tint text-lm-text3 border-lm-border line-through decoration-lm-text3/40"
                    }`}
                  >
                    {k.live ? <CheckCircle2 className="w-2.5 h-2.5" /> : <Circle className="w-2.5 h-2.5" />}
                    {k.text}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </SectionCard>

      {/* ICPs */}
      <SectionCard title={`Ideal customer profiles (${icps.length})`} icon={Users}>
        {icps.length === 0 ? (
          <div className="p-3"><Val value={null} /></div>
        ) : (
          <div className="divide-y divide-lm-border/40">
            {icps.map((i, idx) => (
              <div key={i.icp_id || idx} className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm text-lm-text">{i.name || `ICP ${idx + 1}`}</div>
                  <div className="text-[10px] font-mono text-lm-text3">{i.icp_id}</div>
                </div>
                {i.description && <p className="text-sm text-lm-text2 mb-2">{i.description}</p>}
                <div className="space-y-1.5">
                  {i.role_titles?.length > 0 && <MiniKV k="Roles"><ChipList items={i.role_titles} /></MiniKV>}
                  {i.industries?.length > 0 && <MiniKV k="Industries"><ChipList items={i.industries} /></MiniKV>}
                  {i.company_sizes?.length > 0 && <MiniKV k="Company sizes"><ChipList items={i.company_sizes} /></MiniKV>}
                  {i.pain_points?.length > 0 && <MiniKV k="Pain points"><ChipList items={i.pain_points} tint /></MiniKV>}
                  {i.buying_triggers?.length > 0 && <MiniKV k="Buying triggers"><ChipList items={i.buying_triggers} /></MiniKV>}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Competitors */}
      <SectionCard title={`Competitors identified (${competitors.length})`} icon={Target}>
        {competitors.length === 0 ? (
          <div className="p-3"><Val value={null} /></div>
        ) : (
          <div className="divide-y divide-lm-border/40">
            {competitors.map((c, i) => (
              <div key={c.competitor_id || i} className="px-3 py-2 flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-lm-primary mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{c.name || c.handle || c.url || `Competitor ${i + 1}`}</div>
                  {(c.url || c.handle) && (
                    <div className="text-[11px] text-lm-text3 font-mono truncate">
                      {c.url || c.handle}
                    </div>
                  )}
                  {c.notes && <div className="text-xs text-lm-text2 mt-0.5">{c.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function MiniKV({ k, children }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <div className="w-28 shrink-0 text-[10px] font-mono uppercase tracking-widest text-lm-text3 pt-0.5">{k}</div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

// ================= Scans =================
function ScansTab({ scans }) {
  if (!scans || scans.length === 0) {
    return <div className="text-sm text-lm-text3 py-6 text-center">No scans yet.</div>;
  }
  return (
    <SectionCard title={`Recent scans (${scans.length})`}>
      <div>
        {scans.map((s) => (
          <div key={s.scan_id} className="px-3 py-2 border-b border-lm-border/40 last:border-b-0 text-xs">
            <div className="flex items-center gap-3">
              <span className="font-mono text-lm-text3 whitespace-nowrap">{s.started_at ? new Date(s.started_at).toLocaleString() : "—"}</span>
              <span className="flex-1 truncate text-lm-text">{s.query || s.intent || "—"}</span>
              <span className="font-mono font-semibold text-lm-primary whitespace-nowrap">{s.result_count ?? s.filtered ?? 0} res</span>
            </div>
            {(s.platforms || []).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {(s.platforms || []).map((p) => (
                  <span key={p} className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 bg-lm-tint text-lm-text2 rounded border border-lm-border">{p}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ================= Ledger =================
function LedgerTab({ ledger }) {
  if (!ledger || ledger.length === 0) {
    return <div className="text-sm text-lm-text3 py-6 text-center">No transactions.</div>;
  }
  return (
    <SectionCard title={`Credit ledger (${ledger.length})`}>
      <div className="max-h-96 overflow-y-auto">
        {ledger.map((tx) => (
          <div key={tx.tx_id} className="flex items-center gap-3 px-3 py-2 border-b border-lm-border/40 last:border-b-0 text-xs">
            <span className="font-mono text-lm-text3 whitespace-nowrap">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : "—"}</span>
            <span className="flex-1 truncate font-mono">{tx.reason || "—"}</span>
            <span className={`font-mono font-semibold ${tx.delta > 0 ? "text-emerald-700" : "text-red-700"}`}>{tx.delta > 0 ? "+" : ""}{tx.delta}</span>
            <span className="font-mono text-lm-text3 w-12 text-right">{tx.balance_after ?? "—"}</span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ============== Send reminder button (onboarding nudge) ==============
function NudgeButton({ userId, nudge, onSent }) {
  const [busy, setBusy] = useState(false);
  const nextStep = nudge?.next_step;
  const lastAt = nudge?.last_nudge_at;
  const cooldownH = nudge?.cooldown_hours || 48;

  // If user has completed onboarding, don't render the button at all
  if (!nextStep) {
    return (
      <span
        data-testid="drawer-nudge-complete"
        className="text-xs px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 inline-flex items-center gap-1.5"
      >
        <CheckCircle2 className="w-3.5 h-3.5" /> Onboarding complete
      </span>
    );
  }

  // Cooldown state
  let cooldownRemainingH = 0;
  if (lastAt) {
    const elapsedH = (Date.now() - new Date(lastAt).getTime()) / 3600_000;
    cooldownRemainingH = Math.max(0, Math.ceil(cooldownH - elapsedH));
  }
  const onCooldown = cooldownRemainingH > 0;

  const send = async () => {
    if (onCooldown || busy) return;
    setBusy(true);
    try {
      const r = await adminAxios().post(`/users/${userId}/nudge`);
      if (r.data.delivered) {
        toast.success(`Reminder sent — step: ${r.data.step.replace(/_/g, " ")}`);
      } else {
        toast.warning("Nudge logged, but email service returned an error. Check backend logs.");
      }
      onSent && onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nudge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={send}
        disabled={busy || onCooldown}
        data-testid="drawer-nudge-send"
        title={onCooldown
          ? `Cooldown — next nudge available in ~${cooldownRemainingH}h`
          : `Sends email with deep-link to the ${nextStep.key.replace(/_/g, " ")} step`}
        className={`text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 border font-semibold transition-all ${
          onCooldown
            ? "bg-lm-tint text-lm-text3 border-lm-border cursor-not-allowed"
            : "bg-lm-primary text-white border-lm-primary hover:bg-lm-primaryHover"
        } disabled:opacity-70`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
        {nextStep.button_label}
      </button>
      {lastAt && (
        <span
          data-testid="drawer-nudge-last"
          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-lm-text3"
          title={`Last nudge: ${new Date(lastAt).toLocaleString()} · step: ${nudge.last_nudge_step || "—"}`}
        >
          <Clock className="w-3 h-3" />
          Last nudge {relativeTime(lastAt)}
        </span>
      )}
    </div>
  );
}

